import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Sword, FirstAid, Package, Leaf, Backpack as BackpackIcon } from "@phosphor-icons/react";

const catStyle = {
  weapon: { icon: Sword, tone: "text-tactical-danger", border: "border-tactical-danger/40", label: "Waffe" },
  consumable: { icon: FirstAid, tone: "text-tactical-success", border: "border-tactical-success/40", label: "Verbrauchbar" },
  drug: { icon: Leaf, tone: "text-tactical-warn", border: "border-tactical-warn/40", label: "Drogen" },
  item: { icon: Package, tone: "text-white", border: "border-tactical-border", label: "Item" },
};

export default function Inventory() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/character/inventory").then(r => setItems(r.data)).catch(() => toast.error("Fehler beim Laden"));
  }, []);

  const totalWeight = items.reduce((a, b) => a + (b.weight * b.quantity), 0);
  const totalSlots = 24;
  const usedSlots = items.length;

  // Fill empty slots
  const slots = [...items, ...Array(Math.max(0, totalSlots - items.length)).fill(null)];

  return (
    <div>
      <PageHeader
        eyebrow="04 // INVENTORY"
        title="Inventar"
        description={`${usedSlots} / ${totalSlots} Slots belegt · ${totalWeight.toFixed(1)} kg`}
        right={
          <div className="flex items-center gap-3">
            <BackpackIcon size={28} className="text-volt" weight="duotone" />
            <div className="font-mono text-right">
              <div className="text-[10px] text-white/40 uppercase tracking-widest">Kapazität</div>
              <div className="text-2xl font-black">{Math.round((usedSlots / totalSlots) * 100)}%</div>
            </div>
          </div>
        }
      />

      <div className="p-4 md:p-8 lg:p-12">
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 stagger">
          {slots.slice(0, totalSlots).map((it, idx) => {
            if (!it) {
              return (
                <div key={`empty-${idx}`} className="aspect-square border border-tactical-border border-dashed bg-tactical-surface/30 scanline" />
              );
            }
            const style = catStyle[it.category] || catStyle.item;
            const Icon = style.icon;
            return (
              <div
                key={it.id}
                data-testid={`inv-slot-${it.name}`}
                className={`aspect-square border ${style.border} bg-tactical-surface p-2 flex flex-col justify-between hover:bg-tactical-elev transition-colors duration-150 relative group`}
                title={`${it.label} — ${it.category}`}
              >
                <Icon size={22} className={style.tone} weight="duotone" />
                <div>
                  <div className="font-mono text-[9px] text-white/50 uppercase tracking-wider truncate">{style.label}</div>
                  <div className="text-xs font-medium truncate leading-tight">{it.label}</div>
                </div>
                <div className="absolute top-1 right-1 font-mono text-xs font-bold bg-black/80 border border-white/20 px-1.5 py-0.5">
                  {it.quantity}x
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="mt-10 border border-tactical-border bg-tactical-surface">
            <div className="px-4 py-3 border-b border-tactical-border font-mono text-xs text-white/50 uppercase tracking-widest">
              Detailübersicht
            </div>
            <div className="divide-y divide-tactical-border">
              {items.map(it => {
                const style = catStyle[it.category] || catStyle.item;
                const Icon = style.icon;
                return (
                  <div key={it.id} className="px-4 py-3 flex items-center gap-4 hover:bg-tactical-elev transition-colors">
                    <Icon size={18} className={style.tone} />
                    <div className="flex-1 grid grid-cols-4 gap-4 font-mono text-sm">
                      <div className="text-white">{it.label}</div>
                      <div className="text-white/50">{style.label}</div>
                      <div className="text-white/50">{it.quantity}x</div>
                      <div className="text-right text-white/50">{(it.weight * it.quantity).toFixed(1)} kg</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
